import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronDown,
  ChevronUp,
  Plus,
  SlidersHorizontal,
  Truck,
  X,
} from "lucide-react";

import DashboardLayout from "src/components/layout/dashboard/Layout";
import TableV2 from "src/components/ui/TableV2";
import CardHeader from "src/components/common/card/CardHeader";
import Button from "src/components/ui/Button";
import FilterSelect from "src/components/ui/FilterSelect";
import Card from "src/components/common/card/Card";
import PageSizeSelect from "src/components/ui/PageSizeSelect";

import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as stockActions from "src/redux/slices/stockSlice";
import * as warehouseActions from "src/redux/slices/warehouseSlice";
import useWarehouseLabel from "src/hooks/useWarehouseLabel";
import { formatCreatedBy } from "src/utils/common";
import { formatDateTime } from "src/utils/date-time";

const StockTransferListPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [fromWarehouseId, setFromWarehouseId] = useState("");
  const [toWarehouseId, setToWarehouseId] = useState("");
  const [limit, setLimit] = useState(10);

  const { stockTransferList } = useAppSelector((state) => state.stock);
  const { warehouseList } = useAppSelector((state) => state.warehouse);
  const { singular: warehouseLabel, plural: warehouseLabelPlural } = useWarehouseLabel();
  const {
    user: { data: user },
  } = useAppSelector((state) => state.authTenant);
  const permissions = user?.roleDetails?.permissions || user?.permissions || {};
  const isTenantAdmin =
    user?.role === "admin" ||
    user?.roleDetails?.name?.toLowerCase() === "admin";
  const canCreate =
    isTenantAdmin || (permissions?.transfers || []).includes("create");

  const rows = stockTransferList?.data?.data || [];

  useEffect(() => {
    dispatch(warehouseActions.getWarehouseListRequest({ page: 1, limit: 100 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getList(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limit]);

  const getList = (page: number) =>
    dispatch(stockActions.getStockTransferListRequest({ page, limit }));

  const handlePageChange = (page: number) => getList(page);

  const handleClearFilters = () => {
    setFromWarehouseId("");
    setToWarehouseId("");
  };
  const hasActiveFilters = !!(fromWarehouseId || toWarehouseId);

  const warehouseOptions = (warehouseList?.data?.data || []).map((w: any) => ({
    label: w.name,
    value: w._id,
  }));

  const visibleRows = rows.filter((r: any) => {
    if (fromWarehouseId && r.fromWarehouseId?._id !== fromWarehouseId) return false;
    if (toWarehouseId && r.toWarehouseId?._id !== toWarehouseId) return false;
    return true;
  });

  return (
    <DashboardLayout
      meta={{
        title: "Stock Transfers | Invyntra",
        description: `Move stock between ${warehouseLabelPlural.toLowerCase()}.`,
        keywords: `stock transfers, ${warehouseLabelPlural.toLowerCase()}, inventory, invyntra`,
      }}
    >
      <div className="space-y-4">
        {/* Page header */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary-100 text-primary-600 dark:bg-primary-900/40 dark:text-primary-300">
              <Truck size={26} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                Stock transfers
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Move stock between {warehouseLabelPlural.toLowerCase()}.
              </p>
            </div>
          </div>

          {canCreate && (
            <Button rounded="full" onClick={() => navigate("/app/stock/transfers/new")}>
              <Plus size={16} className="mr-1" />
              New transfer
            </Button>
          )}
        </div>

        {/* Filters */}
        <Card>
          <div className="flex w-full items-center justify-between">
            <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              <SlidersHorizontal size={14} />
              Filters
              {hasActiveFilters && (
                <span className="h-1.5 w-1.5 rounded-full bg-primary-500" />
              )}
            </span>
            <Button
              variant="outline"
              size="sm"
              rounded="full"
              onClick={() => setFiltersOpen((o) => !o)}
            >
              {filtersOpen ? "Hide filters" : "Show filters"}
              {filtersOpen ? (
                <ChevronUp size={16} className="ml-1.5" />
              ) : (
                <ChevronDown size={16} className="ml-1.5" />
              )}
            </Button>
          </div>

          {filtersOpen && (
            <div className="mt-4 grid grid-cols-1 gap-4 border-t border-gray-100 pt-4 dark:border-gray-700 sm:grid-cols-2 lg:grid-cols-4">
              <FilterSelect
                variant="boxed"
                label={`From ${warehouseLabel.toLowerCase()}`}
                value={fromWarehouseId}
                onChange={setFromWarehouseId}
                allLabel="Any"
                options={warehouseOptions}
              />
              <FilterSelect
                variant="boxed"
                label={`To ${warehouseLabel.toLowerCase()}`}
                value={toWarehouseId}
                onChange={setToWarehouseId}
                allLabel="Any"
                options={warehouseOptions}
              />
              <div className="flex items-end">
                <Button
                  variant="outline"
                  color="danger"
                  rounded="full"
                  fullWidth
                  onClick={handleClearFilters}
                  disabled={!hasActiveFilters}
                >
                  <X size={15} className="mr-1.5" />
                  Clear filters
                </Button>
              </div>
            </div>
          )}
        </Card>

        {/* List */}
        <Card>
          <CardHeader
            title="Stock transfer list"
            description={`Past transfers between ${warehouseLabelPlural.toLowerCase()}.`}
            actions={<PageSizeSelect value={limit} onChange={setLimit} />}
          />

          <TableV2
            columns={[
              {
                key: "transferId",
                header: "Transfer ID",
                accessor: ({ row }) => <span>#{row.transferId}</span>,
              },
              {
                key: "from",
                header: "From",
                accessor: ({ row }) => row.fromWarehouseId?.name || "-",
              },
              {
                key: "to",
                header: "To",
                accessor: ({ row }) => row.toWarehouseId?.name || "-",
              },
              {
                key: "items",
                header: "Items",
                accessor: ({ row }) => (
                  <div className="space-y-0.5 text-sm">
                    {(row.items || []).map((item: any, idx: number) => (
                      <div key={idx}>
                        {item.productId?.name || "Product"} × {item.quantity}
                      </div>
                    ))}
                  </div>
                ),
              },
              {
                key: "createdBy",
                header: "Created By",
                accessor: ({ row }) => formatCreatedBy(row?.createdBy),
              },
              {
                key: "createdAt",
                header: "Date",
                accessor: ({ row }) => formatDateTime(row.createdAt, "DD/MM/YYYY hh:mm A"),
              },
            ]}
            data={visibleRows}
            pagination={stockTransferList?.data?.pagination}
            onPageChange={handlePageChange}
            loading={stockTransferList.loading}
          />
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default StockTransferListPage;
